import { Direction } from '../types';

export class InputManager {
  private currentDirection: Direction = Direction.None;
  private queuedDirection: Direction = Direction.None;
  private touchStartX = 0;
  private touchStartY = 0;
  private swipeThreshold = 30;
  private dpadCallback: ((dir: Direction) => void) | null = null;

  private keydownHandler = (e: KeyboardEvent) => {
    const dir = this.keyToDirection(e.key);
    if (dir !== Direction.None) {
      e.preventDefault();
      this.queuedDirection = dir;
    }
  };

  private touchStartHandler = (e: TouchEvent) => {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
  };

  private touchEndHandler = (e: TouchEvent) => {
    const dx = e.changedTouches[0].clientX - this.touchStartX;
    const dy = e.changedTouches[0].clientY - this.touchStartY;
    if (Math.abs(dx) < this.swipeThreshold && Math.abs(dy) < this.swipeThreshold) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      this.queuedDirection = dx > 0 ? Direction.Right : Direction.Left;
    } else {
      this.queuedDirection = dy > 0 ? Direction.Down : Direction.Up;
    }
  };

  attach(canvas: HTMLCanvasElement): void {
    window.addEventListener('keydown', this.keydownHandler);
    canvas.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    canvas.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  detach(canvas: HTMLCanvasElement): void {
    window.removeEventListener('keydown', this.keydownHandler);
    canvas.removeEventListener('touchstart', this.touchStartHandler);
    canvas.removeEventListener('touchend', this.touchEndHandler);
  }

  setDirection(dir: Direction): void {
    this.queuedDirection = dir;
  }

  getQueuedDirection(): Direction {
    return this.queuedDirection;
  }

  getCurrentDirection(): Direction {
    return this.currentDirection;
  }

  consumeQueue(): Direction {
    const dir = this.queuedDirection;
    if (dir !== Direction.None) {
      this.currentDirection = dir;
    }
    return this.currentDirection;
  }

  acceptQueue(): void {
    if (this.queuedDirection !== Direction.None) {
      this.currentDirection = this.queuedDirection;
    }
  }

  clearQueue(): void {
    this.queuedDirection = Direction.None;
  }

  reset(): void {
    this.currentDirection = Direction.None;
    this.queuedDirection = Direction.None;
  }

  private keyToDirection(key: string): Direction {
    switch (key) {
      case 'ArrowUp': case 'w': case 'W': return Direction.Up;
      case 'ArrowDown': case 's': case 'S': return Direction.Down;
      case 'ArrowLeft': case 'a': case 'A': return Direction.Left;
      case 'ArrowRight': case 'd': case 'D': return Direction.Right;
      default: return Direction.None;
    }
  }
}
